import {useCallback, useRef, useState, useEffect} from 'react';
import paper from 'paper';
import {RULER_SIZE} from '@/constants/editor';


export function useRulers({onProjectChange, showCrosshair, setCustomGuides, theme = 'dark'}) {
  const rulersCanvasRef = useRef(null);
  const mousePosRef = useRef(null);
  const themeRef = useRef(theme);
  const [draggedGuide, setDraggedGuide] = useState(null);

  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  const drawRulers = useCallback(() => {
    const rulersCanvas = rulersCanvasRef.current;
    if (!rulersCanvas || !paper.view) return;

    const ctx = rulersCanvas.getContext('2d');
    const width = rulersCanvas.width;
    const height = rulersCanvas.height;

    ctx.clearRect(0, 0, width, height);

    const zoom = paper.view.zoom;
    const bounds = paper.view.bounds;

    if (showCrosshair && mousePosRef.current) {
      const {x, y} = mousePosRef.current;
      ctx.strokeStyle = themeRef.current === 'dark' ? '#fde68a' : '#92400e';
      ctx.lineWidth = 0.8;
      ctx.setLineDash([3, 3]);

      ctx.beginPath();
      ctx.moveTo(RULER_SIZE, y);
      ctx.lineTo(width, y);
      ctx.moveTo(x, RULER_SIZE);
      ctx.lineTo(x, height);
      ctx.stroke();

      ctx.setLineDash([]);
    }

    const isDark = themeRef.current === 'dark';
    ctx.fillStyle = isDark ? 'rgba(29,29,34,0.7)' : '#f8fafc';
    ctx.fillRect(0, 0, width, RULER_SIZE);
    ctx.fillRect(0, 0, RULER_SIZE, height);

    ctx.strokeStyle = isDark ? '#83838a' : '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, RULER_SIZE);
    ctx.lineTo(width, RULER_SIZE);
    ctx.moveTo(RULER_SIZE, 0);
    ctx.lineTo(RULER_SIZE, height);
    ctx.stroke();

    ctx.fillStyle = isDark ? '#41414a' : '#f1f5f9';
    ctx.fillRect(0, 0, RULER_SIZE, RULER_SIZE);

    let stepMM = 10;
    if (zoom < 0.3) stepMM = 100;
    else if (zoom < 0.8) stepMM = 50;
    else if (zoom > 3) stepMM = 2;
    else if (zoom > 8) stepMM = 1;

    ctx.fillStyle = isDark ? '#d8d8e3' : '#6b707e';
    ctx.font = '9px Inter, sans-serif';

    const startX = Math.floor(bounds.left / stepMM) * stepMM;
    const endX = Math.ceil(bounds.right / stepMM) * stepMM;

    for (let x = startX; x <= endX; x += stepMM) {
      const screenX = RULER_SIZE + (x - bounds.left) * zoom;
      if (screenX < RULER_SIZE || screenX > width) continue;

      ctx.beginPath();
      ctx.moveTo(screenX, RULER_SIZE);
      ctx.lineTo(screenX, RULER_SIZE - (x % (stepMM * 5) === 0 ? 10 : 5));
      ctx.stroke();

      if (x % (stepMM * 5) === 0) {
        ctx.fillText(`${x}`, screenX + 2, 12);
      }
    }

    const startY = Math.floor(bounds.top / stepMM) * stepMM;
    const endY = Math.ceil(bounds.bottom / stepMM) * stepMM;

    for (let y = startY; y <= endY; y += stepMM) {
      const screenY = RULER_SIZE + (y - bounds.top) * zoom;
      if (screenY < RULER_SIZE || screenY > height) continue;

      ctx.beginPath();
      ctx.moveTo(RULER_SIZE, screenY);
      ctx.lineTo(RULER_SIZE - (y % (stepMM * 5) === 0 ? 10 : 5), screenY);
      ctx.stroke();

      if (y % (stepMM * 5) === 0) {
        ctx.save();
        ctx.translate(12, screenY - 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(`${y}`, 0, 0);
        ctx.restore();
      }
    }

    if (draggedGuide) {
      ctx.strokeStyle = isDark ? '#fbbf24' : '#d97706';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();

      if (draggedGuide.type === 'horizontal') {
        ctx.moveTo(RULER_SIZE, draggedGuide.currentPos);
        ctx.lineTo(width, draggedGuide.currentPos);
      } else if (draggedGuide.type === 'vertical') {
        ctx.moveTo(draggedGuide.currentPos, RULER_SIZE);
        ctx.lineTo(draggedGuide.currentPos, height);
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [draggedGuide, showCrosshair]);

  const createGuideLine = useCallback(
    (type, projectPoint) => {
      setCustomGuides((prev) => [
        ...prev,
        {
          id: `guide_${Date.now()}`,
          type,
          x: projectPoint.x,
          y: projectPoint.y,
          angle: type === 'horizontal' ? 0 : 90,
        },
      ]);
      onProjectChange?.();
    },
    [onProjectChange, setCustomGuides]
  );

  const handleRulerMouseDown = useCallback((e) => {
    if (!rulersCanvasRef.current) return;

    const rect = rulersCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let guideType = null;

    if (y <= RULER_SIZE && x > RULER_SIZE) {
      guideType = 'horizontal';
    } else if (x <= RULER_SIZE && y > RULER_SIZE) {
      guideType = 'vertical';
    }

    if (guideType) {
      if (e.target.setPointerCapture) {
        e.target.setPointerCapture(e.pointerId);
      }

      setDraggedGuide({
        type: guideType,
        currentPos: guideType === 'horizontal' ? y : x,
      });
    }
  }, []);

  const handleRulerMouseMove = useCallback(
    (e) => {
      if (!rulersCanvasRef.current) return;

      const rect = rulersCanvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      mousePosRef.current = {x, y};

      if (draggedGuide) {
        const newPos = draggedGuide.type === 'horizontal' ? y : x;
        setDraggedGuide((prev) => ({...prev, currentPos: newPos}));
      }

      drawRulers();
    },
    [draggedGuide, drawRulers]
  );

  const handleRulerMouseUp = useCallback(
    (e) => {
      if (!draggedGuide || !rulersCanvasRef.current) return;

      if (e.target.releasePointerCapture) {
        e.target.releasePointerCapture(e.pointerId);
      }

      const rect = rulersCanvasRef.current.getBoundingClientRect();
      const canvasPoint = new paper.Point(e.clientX - rect.left, e.clientY - rect.top);

      const isDroppedOnCanvas =
        (draggedGuide.type === 'horizontal' && canvasPoint.y > RULER_SIZE) ||
        (draggedGuide.type === 'vertical' && canvasPoint.x > RULER_SIZE);

      if (isDroppedOnCanvas) {
        const viewPoint = new paper.Point(
          canvasPoint.x - RULER_SIZE,
          canvasPoint.y - RULER_SIZE
        );
        let projectPoint = paper.view.viewToProject(viewPoint);
        const snapTolerance = 8 / paper.view.zoom;
        if (draggedGuide.type === 'vertical' && Math.abs(projectPoint.x) <= snapTolerance) {
          projectPoint.x = 0;
        } else if (draggedGuide.type === 'horizontal' && Math.abs(projectPoint.y) <= snapTolerance) {
          projectPoint.y = 0;
        }

        createGuideLine(draggedGuide.type, projectPoint);
      }

      setDraggedGuide(null);
      drawRulers();
    },
    [draggedGuide, createGuideLine, drawRulers]
  );

  const handleMouseLeave = useCallback(() => {
    mousePosRef.current = null;
    drawRulers();
  }, [drawRulers]);

  return {
    rulersCanvasRef,
    drawRulers,
    handleRulerMouseDown,
    handleRulerMouseMove,
    handleRulerMouseUp,
    handleMouseLeave,
  };
}